/**
 * Componente: UsuariosScreen
 * 
 * Descripción:
 * Este componente es la pantalla principal de administración de usuarios en la aplicación.
 * Permite visualizar, filtrar y administrar los usuarios del sistema mediante una tabla dinámica.
 * Incluye funcionalidades para:
 * 
 * - Listar usuarios activos o inactivos según un checkbox.
 * - Mostrar información detallada de cada usuario (ID, Código, Estado, Usuario, Nombre, Mail, Rol, Áreas de Consulta y Gerencia).
 * - Editar usuarios mediante un modal.
 * - Agregar usuarios mediante un modal.
 * - Copiar la contraseña de un usuario al portapapeles de forma segura, utilizando cifrado y desencriptado AES.
 * - Agregar firma a un usuario mediante un modal dedicado.
 * - Filtrar usuarios activos o inactivos con un checkbox y actualizar la tabla dinámicamente.
 * - Mostrar notificaciones de acciones mediante un componente Toast.
 * 
 * Tecnologías y librerías utilizadas:
 * - React (Hooks: useState, useEffect, useRef, useContext)
 * - PrimeReact (Tabla AgGrid, Botones, Tag, Toast, Checkbox)
 * - CryptoJS (para desencriptar contraseñas)
 * - Servicios propios de la aplicación para manejar API, idioma, configuración y cifrado
 * - SCSS para estilos específicos de la pantalla
 * 
 * Flujo general:
 * 1. Al montar el componente, se establece el título de la página y se cargan los usuarios activos por defecto.
 * 2. Se muestra una tabla con los usuarios y acciones disponibles.
 * 3. Cada fila incluye botones para editar, copiar contraseña o agregar firma.
 * 4. Los modales de agregar o editar usuarios se abren según la acción seleccionada.
 * 5. La tabla se actualiza dinámicamente según cambios de estado o filtros.
 */

import React, { useState, useEffect, useRef, useContext } from "react";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import CryptoJS from 'crypto-js';

// Componentes para CRUD de usuarios
import ModalAgregarUsuario from "./ModalScreenUsuario/ModalAgregarUsuario";
import ModalEditarUsuario from "./ModalScreenUsuario/ModalEditarUsuario";

// Servicios generales de la apliaccion
import { Button } from "primereact/button";
import { getPassUser, getUsuariosA } from "../../../API/IT/Usuarios/UsersRequests";
import AgGrid from "../../../Base/Components/Tables/AgGrid/AgGrid";
import { encryptor, getSymmetricKey } from "../../../Base/Services/Crypto/Crypto";
import { toastShow } from "../../../Base/Services/ToastService";
import { setTitlePage } from "../../../Base/Services/Config/ConfigService";
import { getLabel } from "../../../Base/Services/Language/LanguageService";
import { baseLang } from "../../../Base/Lang/BaseLang";
import { ConfigContext } from "../../../Base/Context/ConfigContext/ConfigContext";
import { Checkbox } from "primereact/checkbox";
import Card from "../../../Base/Components/Card/Card";
import { getRoles } from "../../../API/IT/Roles/RolesRequests";
import './UsuariosStyles.scss'
import AgregarFirma from './AgregarFirma'

export const UsuariosScreen = () => {

    const toast = useRef(null); // Referencia para el toast
    const { config } = useContext(ConfigContext) // Contexto de configuracion global

    /**Estados para manejo de usuarios y edicion */
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userEdit, setuserEdit] = useState(null);
    const [selected, setSelected] = useState(null);
    const [showAddFirma, setShowAddFirma] = useState(false);
    const [roles, setRoles] = useState([]);
    const [checked, setChecked] = useState(true);

    /**
     * Funcion para obtener usuarios y roles
     * @param {number} IdStatus - IdStatus para filtrar activos/inactivos 
     */
    const getUsers = async (IdStatus = checked ? 82 : 83) => {
        setLoading(true); // Activa el loading
        setUsuarios([]); // Limpia temporalmente la lista

        const r = await getRoles(); // Obtiene los roles desde API
        setRoles(r);

        // Llama al request con el IdStatus correcto según checkbox
        console.log(IdStatus);
        
        const u = await getUsuariosA(IdStatus);
        // Transforma los campos "Secciones" y "AreasConsulta" a arrays 
        const t = u.map(e => ({
            ...e,
            IdSecciones: e?.Secciones ? e?.Secciones.split(',') : [],
            AreasConsulta: e?.AreasConsulta ? e?.AreasConsulta.split(',').map(e => parseInt(e)) : []
        }));

        setUsuarios(t); // Actualiza estado con usuarios transformados
        setLoading(false); // Desactiva loading
    }

    /**
     * useEffect inicial: establece titulo y carga usuarios
     */
    useEffect(() => {
        setTitlePage(getLabel(baseLang, 'users', config.language)) // Cambia el titulo de pagina segun el Tab
        getUsers(82); // Carga solo los usuarios activos
        // <ModalMessage usuarios= {usuarios}/>
    }, []);

    /**
     * Funcion para renderizar el estado de usuario con Tag
     * @param {object} rowData - Objeto usuario 
     * @returns JSX del Tag con color segun estado 
     */
    const statusBodyTemplate = (rowData) => {
        if (rowData.StatusName) {
            if (rowData.StatusName === "Active") {
                return <Tag className="col-12" severity="success" value={rowData.StatusName} />
            }
            else {
                return <Tag className="col-12" severity="danger" value={rowData.StatusName} />
            }
        } else {
            return null
        }
    }

    /**
     * Componente interno para botones de accion por fila
     * @param {object} rowData - Objeto usuario 
     * @returns {Promise<Objetc>} Obtiene la password descriptada
     */
    const Botones = ({ rowData }) => {
        const [password, setPassword] = useState(''); // Estado temporal de la password

        /**
         * Obtiene y copia la password del usuario encriptada
         * @param {object} rowData - Usuario a obtener password 
         */
        const getPassworduser = async (rowData) => {
            const key = await getSymmetricKey(); // Obtiene la clave simetrica
            const keyEncryptada = await encryptor(JSON.stringify({ key: key.exportKey, idUser: rowData.idUser }));
            const pass = await getPassUser(keyEncryptada); // Obtiene la password desde API
            var decrypted = CryptoJS.AES.decrypt(pass, key.exportKey).toString(CryptoJS.enc.Utf8);
            setPassword(decrypted);
            //Copiar al portapapeles
            const text = document.createElement('textarea');
            text.value = decrypted;
            text.setAttribute("readonly", "");
            document.body.appendChild(text)
            text.select()
            document.execCommand('copy')
            document.body.removeChild(text);

            toastShow(toast, 'success', 'Contraseña copiada')

            // Limpiar password temporal despues de 5 segundos
            setTimeout(() => {
                setPassword(null)
            }, 5 * 1000);
        }
        return <div className="d-flex" >
            <Button icon="pi pi-pencil" className="p-button-infop p-button-text p-0" onClick={e => {
                setuserEdit(rowData);
            }} />
            <Button icon="pi pi-lock" className=" p-button-infop p-button-text  p-0" onClick={(e) => {
                getPassworduser(rowData)
            }} tooltip={password} tooltipOptions={{ position: 'left' }} />

        </div>
    }

    /**
     * Configuracion de columnas de la tabla
     */
    const columns = [
        {
            header: 'IdUser',
            field: 'idUser',
            className: 'Xsmall'
        },
        {
            header: 'Codigo',
            field: 'idPersonal',
            className: 'Xsmall'
        },
        {
            header: 'Estado',
            field: 'StatusName',
            Format: 'Template',
            className: 'Xsmall',
            body: e => statusBodyTemplate(e)
        },
        {
            header: 'Usuario',
            field: 'Usuario',
        },
        {
            header: 'Nombre',
            field: 'UserName',
            Sumary: 'count'
        },
        {
            header: 'Mail',
            field: 'Mail',
            className: 'large'
        },
        {
            header: 'Rol',
            field: 'Rol',
        },
        // {
        //     header: 'Rol',
        //     field: 'Rol',
        //     editable: true,
        //     value: 'IdRol',
        //     label: 'Rol',
        //     items: roles,
        //     Format: 'AutoComplete'
        // },
        {
            header: 'AreasConsulta',
            field: 'AreasNombre',
        },

        {
            header: 'Gerencia',
            field: 'NombresSeccion',
            // flex: 1
        },
        { //Columna para abrir modalde agregar firma
            field: "IdUser",
            className: "",
            header: "",
            Format: "Botones",
            frozen: true,
            alignFrozen: "right",
            filter: false,
            Botones: [
                {
                    icon: "ri-draft-line",
                    tooltip: "Agregar Firma",
                    onClick: (rowData) => {
                        setSelected(rowData)
                        setShowAddFirma(true);
                    },
                },
            ],
        },

        { //Columna con botones de accion de cada Usuario
            field: 'Botones',
            header: '',
            Format: 'Template',
            body: e => e.idUser ? <Botones rowData={e} /> : null,
            frozen: true,
            alignFrozen: 'right',
            className: 'xsmall',

        },

    ]

    /**
     * Configuracion general de la tabla
     */
    const table = {
        Columns: columns,
        Data: usuarios,
        loading: loading
    }

    return (
        <div className="usuarios__contenedor p-2" >
            {/* Modal de edicion de usuario */}
            <ModalEditarUsuario getUsers={getUsers} userEdit={userEdit} setuserEdit={setuserEdit} />
            {/* Componente Toast */}
            <Toast position="bottom-right" ref={toast}></Toast>
            {/* Contenedor principal con título y tabla */}
            <Card
                titulo={<div className="botones">
                    <h3>{getLabel(baseLang, 'users', config.language)}</h3>
                    <Button icon='pi pi-search' className="mx-2" onClick={getUsers} />
                    <ModalAgregarUsuario usuarios={getUsers} />
                    <Checkbox
                        style={{ transform: 'scale(1.5)', display: 'flex', marginTop: '8px' }}
                        checked={checked}
                        onChange={e => {
                            setChecked(e.checked);

                            // Checkbox controla el IdStatus
                            const IdStatus = e.checked ? 82 : 83;
                            getUsers(IdStatus);
                        }}
                    >
                        Solo Activos
                    </Checkbox>

                </div>}
                contenido={<div className="p-4" style={{ height: '90vh' }}>
                    <AgGrid table={table} />

                </div>}
            />
            
            {/* Modal para agregar firma */}
            {showAddFirma && (
                <AgregarFirma
                    showAddFirma={showAddFirma}
                    setShowAddFirma={setShowAddFirma}
                    userEdit={selected}
                    toast={toast}
                />
            )}
        </div>
    )
}

export default UsuariosScreen;